// Inline license required view — shown as page content in the Dashboard
// when the signed-in user has no active license.

import { Capacitor } from "@capacitor/core";
import {
  Apple,
  Check,
  CreditCard,
  Globe,
  Loader2,
  RefreshCw,
  Smartphone,
} from "lucide-react";
import { useState } from "react";
import toast from "react-hot-toast";
import { useQueryClient } from "../hooks/useDataCache";
import { useLicense } from "../hooks/useLicense";
import { markExplicitSignOut } from "../lib/authSignals";
import { supabase } from "../lib/supabase";

const GOOGLE_PLAY_URL =
  "https://play.google.com/store/apps/details?id=com.hillardworks.pantry";
const APP_STORE_URL = "https://apps.apple.com/us/app/pantry-kitchen-inventory-app/id6829573379";

// Web only: Stripe and store links must never be shown inside the native
// apps (App Store guideline 3.1.1). Native users see a retry screen instead.
export default function LicenseRequired() {
  return Capacitor.isNativePlatform() ? (
    <NativeLicenseRequired />
  ) : (
    <WebLicenseRequired />
  );
}

function NativeLicenseRequired() {
  const { activateApp } = useLicense();
  const queryClient = useQueryClient();

  const handleRetry = () => {
    activateApp.mutate(
      Capacitor.getPlatform() === "ios" ? "app_store" : "google_play",
      {
        onError: () =>
          toast.error("We couldn't verify your purchase. Please try again."),
      },
    );
  };

  const handleLogout = async () => {
    markExplicitSignOut();
    await supabase.auth.signOut();
    queryClient.clear();
  };

  return (
    <div className="flex flex-col items-center justify-center py-12">
      <Smartphone className="mb-4 h-12 w-12 text-gray-400 dark:text-gray-600" />
      <h2 className="mb-2 text-2xl font-bold text-gray-800 dark:text-gray-100">
        Unable to Verify Purchase
      </h2>
      <p className="mb-6 text-center text-gray-600 dark:text-gray-400">
        We couldn&apos;t confirm your app purchase. Check your connection and
        try again.
      </p>
      <button
        type="button"
        onClick={handleRetry}
        disabled={activateApp.isPending}
        className="flex items-center gap-2 rounded-lg bg-blue-600 px-6 py-3 font-medium text-white shadow-md hover:bg-blue-700 hover:shadow-lg disabled:opacity-50 dark:bg-blue-500 dark:hover:bg-blue-600"
      >
        {activateApp.isPending ? (
          <Loader2 className="h-5 w-5 animate-spin" />
        ) : (
          <RefreshCw className="h-5 w-5" />
        )}
        {activateApp.isPending ? "Verifying..." : "Try Again"}
      </button>
      <button
        type="button"
        onClick={handleLogout}
        className="mt-4 text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
      >
        Sign out
      </button>
    </div>
  );
}

function WebLicenseRequired() {
  const { purchaseWeb } = useLicense();
  const [isLoading, setIsLoading] = useState(false);

  const handlePurchaseWeb = async () => {
    if (isLoading) return;
    setIsLoading(true);
    try {
      await purchaseWeb.mutateAsync();
      // User will be redirected to Stripe Checkout
    } catch (error: unknown) {
      toast.error(
        error instanceof Error ? error.message : "Failed to start purchase",
      );
      setIsLoading(false);
    }
  };

  return (
    <div className="mx-auto max-w-7xl py-8">
      {/* Header */}
      <div className="mb-4 flex items-center justify-center">
        <CreditCard className="h-12 w-12 text-yellow-600 dark:text-yellow-400" />
      </div>

      <h2 className="mb-2 text-center text-2xl font-bold text-gray-800 dark:text-gray-100">
        One-Time Purchase Required
      </h2>
      <p className="mb-12 text-center text-gray-600 dark:text-gray-400">
        Purchase a license to create and manage a household
      </p>

      {/* Purchase Options */}
      <div className="mb-6 flex flex-col gap-12 md:flex-row md:items-stretch">
        {/* Web License Option */}
        <div className="flex flex-1 flex-col rounded-lg border-2 border-gray-200 bg-white p-5 dark:border-gray-700 dark:bg-gray-800">
          <div className="mb-3 flex items-start gap-3">
            <Globe className="mt-1 h-6 w-6 shrink-0 text-blue-500" />
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100">
                Web License
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                For web and PWA usage
              </p>
            </div>
            <div className="text-right">
              <div className="text-2xl font-bold text-gray-800 dark:text-gray-100">
                $9.99
              </div>
              <div className="text-xs text-gray-500">one-time</div>
            </div>
          </div>
          <ul className="mb-4 ml-9 space-y-2 text-sm">
            <FeatureItem text="Works in any browser" />
            <FeatureItem text="Install as a Progressive Web App (PWA)" />
            <FeatureItem text="Create and manage your household" />
            <FeatureItem text="Invite up to 5 household members (free for them)" />
            <FeatureItem text="Real-time sync across devices" />
            <FeatureItem text="Recipe search by ingredients" />
          </ul>
          <div className="mt-auto pt-4">
            <button
              type="button"
              onClick={handlePurchaseWeb}
              disabled={isLoading || purchaseWeb.isPending}
              className="flex w-full items-center justify-center gap-2 rounded-lg bg-blue-600 py-3 font-medium text-white transition hover:bg-blue-700 disabled:opacity-50"
            >
              {isLoading || purchaseWeb.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin text-white" />
              ) : (
                <Globe className="h-4 w-4" />
              )}
              {isLoading || purchaseWeb.isPending
                ? "Processing..."
                : "Purchase Web License"}
            </button>
          </div>
        </div>

        {/* App License Option */}
        <div className="relative flex flex-1 flex-col rounded-lg border-2 border-blue-500 bg-blue-50 p-5 dark:border-blue-600 dark:bg-blue-900/20">
          <div className="absolute -top-3 left-4 rounded-full bg-blue-500 px-3 py-1 text-xs font-bold text-white">
            BEST VALUE
          </div>
          <div className="mb-3 flex items-start gap-3">
            <Smartphone className="mt-1 h-6 w-6 shrink-0 text-blue-500" />
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100">
                Native App License
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Available on Google Play and the App Store
              </p>
            </div>
            <div className="text-right">
              <div className="text-2xl font-bold text-gray-800 dark:text-gray-100">
                $14.99
              </div>
              <div className="text-xs text-gray-500">one-time</div>
            </div>
          </div>
          <ul className="mb-4 ml-9 space-y-2 text-sm">
            <FeatureItem text="Everything in Web License" />
            <FeatureItem text="Native Android & iOS apps" />
            <FeatureItem text="Better offline support" />
            <FeatureItem text="Faster performance" />
            <FeatureItem text="Unlocks web app usage" highlighted />
          </ul>
          <div className="mt-auto flex flex-col gap-3 pt-4 sm:flex-row">
            <a
              href={GOOGLE_PLAY_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="flex flex-1 items-center justify-center gap-2 rounded-lg bg-blue-600 py-3 font-medium text-white transition-colors hover:bg-blue-700"
            >
              <Smartphone className="h-4 w-4" />
              Google Play
            </a>
            <a
              href={APP_STORE_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="flex flex-1 items-center justify-center gap-2 rounded-lg bg-blue-600 py-3 font-medium text-white transition-colors hover:bg-blue-700"
            >
              <Apple className="h-4 w-4" />
              App Store
            </a>
          </div>
        </div>
      </div>

      {/* Info text */}
      <div className="space-y-2 text-center">
        <p className="text-sm text-gray-600 dark:text-gray-400">
          💡 <strong>Invited members always FREE</strong> — Join via web at no
          cost
        </p>
        <p className="text-xs text-gray-500">
          One-time purchase &bull; No subscriptions &bull; Lifetime access
        </p>
      </div>
    </div>
  );
}

function FeatureItem({
  text,
  highlighted = false,
}: {
  text: string;
  highlighted?: boolean;
}) {
  return (
    <li className="flex items-start gap-2">
      <Check
        className={`mt-0.5 h-4 w-4 shrink-0 ${
          highlighted ? "text-blue-500" : "text-green-500"
        }`}
      />
      <span
        className={
          highlighted ? "font-semibold text-blue-600 dark:text-blue-400" : ""
        }
      >
        {text}
      </span>
    </li>
  );
}
